import { useState } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import { useSubmitReview, type ReviewTarget } from '../../hooks/useAPI';

interface ReviewModalProps {
  target: ReviewTarget;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmitted: () => void;
}

export default function ReviewModal({ target, title, subtitle, onClose, onSubmitted }: ReviewModalProps) {
  const { submit, isLoading } = useSubmitReview();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = rating > 0 && !isLoading;

  const doSubmit = async () => {
    setError(null);
    const { error } = await submit(target, rating, comment.trim() || null);
    if (error) { setError(error.message); return; }
    onSubmitted();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-background rounded-t-3xl sm:rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-1 mb-4">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = (hover || rating) >= n;
            return (
              <button
                key={n}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1"
                aria-label={`${n} star`}
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    filled ? 'fill-[#D4F04A] text-[#D4F04A]' : 'text-border'
                  }`}
                />
              </button>
            );
          })}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          maxLength={400}
          placeholder="Share a few words about your experience (optional)"
          className="w-full px-3 py-2 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none text-sm resize-none"
        />
        <div className="text-[11px] text-muted-foreground text-right mt-1">{comment.length}/400</div>

        {error && (
          <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2 mt-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-border hover:bg-secondary text-sm"
          >
            Cancel
          </button>
          <button
            onClick={doSubmit}
            disabled={!canSubmit}
            className="flex-1 h-11 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Submit review
          </button>
        </div>
      </div>
    </div>
  );
}
